import React, { useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/src/ReactCrop.scss'
import { Box, Button, Typography } from '@mui/material'
import { useDocPreview } from 'hooks/useDocPreview'
import { useNotificationStore } from 'store/useNotificationStore'

import CropIcon from 'images/crop'
import RotateIcon from 'images/rotate'
import UndoIcon from 'images/undo'

import {
    ACCEPT_ONLY_IMAGES,
    ONLY_IMAGE_SUPPORTED_TOASTER_CONTENT,
    ERROR_TOASTER_TITLE
} from 'constants/docUploadConstant'
import ReplaceIcon from 'images/ReplaceIcon'
import { MESSAGES } from '../messages/messages'

function getRadianAngle(degreeValue) {
    return (degreeValue * Math.PI) / 180
}

function rotateSize(width, height, rotation) {
    const rotRad = getRadianAngle(rotation)

    return {
        width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
        height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height)
    }
}

const initialCropState = {
    aspect: 1,
    x: 0,
    y: 0,
    height: 100,
    width: 100,
    unit: '%'
}

const ImageCrop = ({ url }) => {
    const { editPreview, setEditPreview, closeEditPreview, handleReuploadFile, isUnsupportedFileType } = useDocPreview()
    const { showNotification } = useNotificationStore((state) => state)
    const imgRef = useRef(null)
    const fileRef = useRef()
    const [crop, setCrop] = useState(initialCropState)

    const [editActionStyle, setEditActionStyle] = useState({
        enableRotation: false,
        enableCrop: false,
        enableUndo: false
    })

    const [enableCrop, setEnableCrop] = useState(false)
    const [changeHistory, setChangeHistory] = useState([])
    const [disableUndoAndRotate, setDisableUndoAndRotate] = useState(false)

    const imageUrl = url
    const scale = 1

    const getEditIconStyling = (iconEnabled) => {
        return {
            color: iconEnabled ? 'primary.main' : 'common.fordTwilight.main',
            '&:hover': {
                color: 'primary.main'
            },
            display: 'flex',
            flexDirection: {
                xs: 'column',
                smd: 'row'
            },
            padding: 1
        }
    }

    const getCroppedImg = (image, crop) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
            throw new Error('No 2d context')
        }

        const scaleX = image?.naturalWidth / image?.width
        const scaleY = image?.naturalHeight / image?.height

        canvas.width = image?.naturalWidth
        canvas.height = image?.naturalHeight

        ctx.translate(image?.naturalWidth / 2, image?.naturalHeight / 2)
        ctx.translate(-image.naturalWidth / 2, -image.naturalHeight / 2)

        ctx.drawImage(image, 0, 0)

        const data = ctx.getImageData(crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY)

        canvas.width = crop.width * scaleX
        canvas.height = crop.height * scaleY

        ctx.putImageData(data, 0, 0)
        return canvas.toDataURL('image/jpeg')
    }

    const getRotatedImage = (image, rotation) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
            throw new Error('No 2d context')
        }

        const rotRad = getRadianAngle(rotation)
        const { width, height } = rotateSize(image?.naturalWidth, image?.naturalHeight, rotation)

        canvas.width = width
        canvas.height = height

        ctx.translate(width / 2, height / 2)
        ctx.rotate(rotRad)
        ctx.translate(-image.naturalWidth / 2, -image.naturalHeight / 2)

        ctx.drawImage(image, 0, 0)

        const data = ctx.getImageData(0, 0, width, height)

        ctx.putImageData(data, 0, 0)
        return canvas.toDataURL('image/jpeg')
    }

    const onImageLoad = () => {
        const cropOption = {
            ...initialCropState,
            imageUrl
        }

        if (!changeHistory.length) {
            setChangeHistory([cropOption])
        }
    }

    const onCropComplete = useCallback(
        (e) => {
            if (e?.height && e?.width) {
                const newChangeHistory = [...changeHistory, { imageUrl }]
                const cropImageUrl = getCroppedImg(imgRef.current, e)
                const updatedPreview = {
                    ...editPreview,
                    base64Data: cropImageUrl.split(',')[1]
                }

                setCrop(initialCropState)
                setChangeHistory(newChangeHistory)
                setEditPreview(updatedPreview)
                setDisableUndoAndRotate(false)
            }
        },
        [editPreview, changeHistory, setEditPreview, imageUrl]
    )

    const finishCropping = () => {
        const updatedPreview = {
            ...editPreview,
            base64Data: imageUrl.split(',')[1]
        }
        closeEditPreview(updatedPreview)
    }

    const handleRetake = () => {
        fileRef.current.click()
    }

    const handleFileSelection = (event) => {
        if (isUnsupportedFileType(event, ACCEPT_ONLY_IMAGES)) {
            showNotification({
                title: ERROR_TOASTER_TITLE,
                content: ONLY_IMAGE_SUPPORTED_TOASTER_CONTENT,
                show: true
            })
            event.target.value = null
        } else {
            handleReuploadFile(event)
        }
    }

    const handleRotation = () => {
        setEnableCrop(false)
        setEditActionStyle({
            enableRotation: true,
            enableCrop: false,
            enableUndo: false
        })

        const cropImageUrl = getRotatedImage(imgRef.current, 90)
        const updatedPreview = {
            ...editPreview,
            base64Data: cropImageUrl.split(',')[1]
        }

        setChangeHistory([...changeHistory, { rotation: -90 }])
        setCrop(initialCropState)
        setEditPreview(updatedPreview)
    }

    const handleCrop = () => {
        setEnableCrop((old) => !old)
        setEditActionStyle({
            enableRotation: false,
            enableCrop: !enableCrop,
            enableUndo: false
        })
    }

    const handleUndo = () => {
        const prevState = changeHistory.pop()
        setChangeHistory([...changeHistory])
        setEditActionStyle({
            enableRotation: false,
            enableCrop: enableCrop && changeHistory.length <= 1,
            enableUndo: changeHistory.length > 1
        })

        const { imageUrl } = { ...prevState, rotation: null }

        if (prevState.rotation) {
            const cropImageUrl = getRotatedImage(imgRef.current, prevState.rotation)
            const updatedPreview = {
                ...editPreview,
                base64Data: cropImageUrl.split(',')[1]
            }
            setCrop(initialCropState)
            setEditPreview(updatedPreview)
        } else if (imageUrl) {
            const updatedPreview = {
                ...editPreview,
                base64Data: imageUrl.split(',')[1]
            }

            setCrop(initialCropState)
            setEditPreview(updatedPreview)
        }
    }

    return (
        <>
            <input
                onChange={handleFileSelection}
                onClick={(event) => {
                    event.target.value = null
                }}
                type="file"
                ref={fileRef}
                accept={ACCEPT_ONLY_IMAGES}
                hidden
            />

            <Box
                sx={{
                    mt: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'center',
                    '.ReactCrop__crop-selection': {
                        backgroundImage: enableCrop ? 'inherit' : 'none !important',
                        cursor: enableCrop ? 'move' : 'default'
                    },
                    '.ReactCrop__drag-handle::after': {
                        width: 16,
                        height: 16
                    }
                }}
            >
                <Box
                    sx={{
                        backgroundColor: 'common.white.main',
                        maxWidth: { xs: 500, md: 655 },
                        width: '100%',
                        margin: 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 8,
                        px: 4,
                        py: 8,
                        mt: 5
                    }}
                >
                    <Box
                        sx={{
                            width: 500,
                            maxWidth: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <ReactCrop
                            src={imageUrl}
                            crop={crop}
                            keepSelection={true}
                            locked={!enableCrop}
                            disabled={!enableCrop}
                            onChange={(_, percentCrop) => {
                                if (percentCrop?.height && percentCrop?.width) {
                                    setCrop(percentCrop)
                                    setDisableUndoAndRotate(true)
                                }
                            }}
                            onComplete={onCropComplete}
                        >
                            <img
                                ref={imgRef}
                                crossOrigin="anonymous"
                                alt="img"
                                src={imageUrl}
                                style={{ transform: `scale(${scale})`, maxHeight: 500 }}
                                onLoad={onImageLoad}
                            />
                        </ReactCrop>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: {
                            xs: 0,
                            smd: 6
                        }
                    }}
                >
                    <Button
                        data-testid="retake"
                        disableRipple
                        onClick={handleRetake}
                        sx={{
                            display: 'flex',
                            flexDirection: {
                                xs: 'column',
                                smd: 'row'
                            },
                            padding: 1
                        }}
                    >
                        <ReplaceIcon />
                        <Typography
                            sx={{
                                color: 'common.charcoal.main',
                                typography: { xs: 'mediumBody3', smd: 'mediumBody3', md: 'boldBody2' },
                                pl: {
                                    smd: 1
                                },
                                pt: {
                                    xs: 1,
                                    smd: 0
                                }
                            }}
                        >
                            {MESSAGES.replace}
                        </Typography>
                    </Button>
                    <Button
                        data-testid="undo"
                        disableRipple
                        disabled={changeHistory.length <= 1 || disableUndoAndRotate}
                        onClick={handleUndo}
                        sx={getEditIconStyling(editActionStyle.enableUndo)}
                        aria-label="Undo"
                    >
                        <UndoIcon />
                        <Typography
                            sx={{
                                color: 'common.charcoal.main',
                                typography: { xs: 'mediumBody3', smd: 'mediumBody3', md: 'boldBody2' },
                                pl: {
                                    smd: 1
                                },
                                pt: {
                                    xs: 1,
                                    smd: 0
                                }
                            }}
                        >
                            {MESSAGES.undo}
                        </Typography>
                    </Button>
                    <Button
                        data-testid="crop"
                        disableRipple
                        onClick={handleCrop}
                        sx={getEditIconStyling(editActionStyle.enableCrop)}
                        aria-label="Crop"
                    >
                        <CropIcon />
                        <Typography
                            sx={{
                                color: 'common.charcoal.main',
                                typography: { xs: 'mediumBody3', smd: 'mediumBody3', md: 'boldBody2' },
                                pl: {
                                    smd: 1
                                },
                                pt: {
                                    xs: 1,
                                    smd: 0
                                }
                            }}
                        >
                            {MESSAGES.crop}
                        </Typography>
                    </Button>
                    {changeHistory?.length > 0 && (
                        <Button
                            disabled={disableUndoAndRotate}
                            data-testid="rotate"
                            disableRipple
                            onClick={handleRotation}
                            sx={getEditIconStyling(editActionStyle.enableRotation)}
                            aria-label="Rotate"
                        >
                            <RotateIcon />
                            <Typography
                                sx={{
                                    color: 'common.charcoal.main',
                                    typography: { xs: 'mediumBody3', smd: 'mediumBody3', md: 'boldBody2' },
                                    pl: {
                                        smd: 1
                                    },
                                    pt: {
                                        xs: 1,
                                        smd: 0
                                    }
                                }}
                            >
                                {MESSAGES.rotate}
                            </Typography>
                        </Button>
                    )}

                    <Button
                        data-testid="done"
                        disableRipple
                        variant="outlined"
                        color="primary"
                        onClick={finishCropping}
                        sx={{
                            width: {
                                xs: 0,
                                smd: '86px'
                            },
                            height: '40px'
                        }}
                    >
                        <Typography variant="boldBody2">{MESSAGES.done}</Typography>
                    </Button>
                </Box>
            </Box>
        </>
    )
}

ImageCrop.propTypes = {
    url: PropTypes.string
}

export default ImageCrop
